import { gql, useMutation } from '@apollo/client'
import { CheckCircle, EmailOutlined, PrintOutlined, SmsOutlined } from '@mui/icons-material'
import { Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material'
import { format } from 'date-fns'
import { FC, ReactElement, useContext, useState } from 'react'

// components
import { ConfirmationModal } from '../../common/ConfirmationModal'
import { Alert } from '../../common/Alert'

// others
import { BillDeliveryMethod, BillStatus, BillTimelineAction, ExternalUserCaseRole } from '../../../../generated'
import { AuthContext } from '../../../context'
import { CardBox } from '../../../theme/styleComponents'
import { enumToLabel, hasAdminAccess, titleize } from '../../../utils'

const MARK_BILL_DELIVERED = gql`
  mutation MarkBillAsDelivered($billId: Int!) {
    markBillAsDelivered(billId: $billId) {
      response {
        status
        message
      }
    }
  }
`

interface DeliveryParty {
  caseRole?: ExternalUserCaseRole | null
  billDeliveryMethods?: BillDeliveryMethod[] | null
  billingAddress?: string | null
  billingEmail?: string | null
  billingPhoneNumber?: string | null
  isArchived?: boolean | null
  externalUserToExternalUserRole: {
    externalUser?: {
      id?: string | null
      displayName?: string | null
      firstName?: string | null
      lastName?: string | null
    } | null
  }
}

interface DeliveryTimeline {
  action: BillTimelineAction
  deliveryMetadata?: { mode?: string | null; status?: string | null } | null
}

interface BillDeliveryDetailsCardProps {
  parties?: DeliveryParty[] | null
  ccEmailBillsTo?: string | null
  billId?: number | null
  billStatus?: BillStatus | null
  lastSentAt?: string | number | Date | null
  billTimelines?: DeliveryTimeline[] | null
  isSplitBill?: boolean | null
  selectedClientId?: string | null
  onRefetch?: () => void
}

const NOT_SET = 'Not set'

const MARK_DELIVERED_STATUSES = [
  BillStatus.Billed,
  BillStatus.SentToClient,
  BillStatus.PartiallyPaid,
  BillStatus.Paid,
  BillStatus.Forwarded
]

const DELIVERY_METHOD_ORDER = [BillDeliveryMethod.Paper, BillDeliveryMethod.Email, BillDeliveryMethod.Text]

/** Delivery label for a bill marked as delivered by printing it. */
const PRINTING_LABEL = 'Printing'

const getMethodConfig = (
  method: BillDeliveryMethod,
  party: DeliveryParty
): { label: string; icon: ReactElement; target?: string | null } => {
  switch (method) {
    case BillDeliveryMethod.Paper:
      return { label: 'Paper', icon: <PrintOutlined fontSize="small" color="action" />, target: party.billingAddress }
    case BillDeliveryMethod.Email:
      return { label: 'Email', icon: <EmailOutlined fontSize="small" color="action" />, target: party.billingEmail }
    case BillDeliveryMethod.Text:
      return { label: 'Text', icon: <SmsOutlined fontSize="small" color="action" />, target: party.billingPhoneNumber }
  }
}

const getClientName = (party: DeliveryParty): string => {
  const { externalUser } = party.externalUserToExternalUserRole || {}
  const { displayName, firstName, lastName } = externalUser || {}
  return displayName || [firstName, lastName].filter(Boolean).join(' ') || 'Client'
}

const BillDeliveryDetailsCard: FC<BillDeliveryDetailsCardProps> = ({
  parties,
  ccEmailBillsTo,
  billId,
  billStatus,
  lastSentAt,
  billTimelines,
  isSplitBill,
  selectedClientId,
  onRefetch
}) => {
  const { currentUser } = useContext(AuthContext)
  const [showMarkDeliveredConfirmation, setShowMarkDeliveredConfirmation] = useState(false)

  const [markBillDelivered, { loading: markingDelivered }] = useMutation(MARK_BILL_DELIVERED, {
    onError: e => Alert.error(e.message),
    onCompleted: data => {
      Alert.success(data?.markBillAsDelivered?.response?.message || 'Bill marked as delivered to client')
      setShowMarkDeliveredConfirmation(false)
      onRefetch?.()
    }
  })

  const canMarkDelivered =
    hasAdminAccess(currentUser) && !!billStatus && MARK_DELIVERED_STATUSES.includes(billStatus)

  const deliveredDate = lastSentAt ? new Date(lastSentAt) : null
  const isDelivered = !!deliveredDate && !isNaN(deliveredDate.getTime())

  // Only active (non-archived) client relationships with a delivery method configured.
  const activeParties = (parties || []).filter(
    party => !party.isArchived && (party.billDeliveryMethods?.length || 0) > 0
  )

  // On split bills, narrow to the currently-selected client (fall back to all active if no match).
  const selectedParties =
    isSplitBill && selectedClientId
      ? activeParties.filter(party => party.externalUserToExternalUserRole?.externalUser?.id === selectedClientId)
      : []
  const deliverableParties = selectedParties.length > 0 ? selectedParties : activeParties

  // Derive the method(s) actually used to deliver:
  //  - Automated email/text sends stamp deliveryMetadata.mode on the timeline (precise).
  //  - Printing a bill marks it delivered with a Paper mode on the "marked as delivered" entry,
  //    shown as "via Printing".
  //  - A manual "mark as delivered" has no channel on the timeline. On a Paper-configured client it
  //    is a paper delivery; any other manual mark stays "manually marked".
  const timelines = billTimelines || []
  const usedMethods = new Set<string>()
  timelines
    .filter(
      timeline =>
        (timeline.action === BillTimelineAction.BillInvoiceEmailSentToClient ||
          timeline.action === BillTimelineAction.BillInvoiceTextSentToClient) &&
        timeline.deliveryMetadata?.status === 'SENT' &&
        !!timeline.deliveryMetadata?.mode
    )
    .forEach(timeline => usedMethods.add(titleize(timeline.deliveryMetadata!.mode as string)))

  const markedAsDelivered = timelines.filter(timeline => timeline.action === BillTimelineAction.BillMarkedAsDelivered)
  // The API returns the stored mode value ("Paper"), not the GraphQL enum name, so compare loosely.
  const isPrintMark = (timeline: DeliveryTimeline) =>
    timeline.deliveryMetadata?.mode?.toUpperCase() === BillDeliveryMethod.Paper
  const wasPrinted = markedAsDelivered.some(isPrintMark)
  const wasManuallyMarked = markedAsDelivered.some(timeline => !isPrintMark(timeline))
  const anyPaperConfigured = deliverableParties.some(party =>
    party.billDeliveryMethods?.includes(BillDeliveryMethod.Paper)
  )
  if (wasPrinted) {
    usedMethods.add(PRINTING_LABEL)
  } else if (wasManuallyMarked && anyPaperConfigured) {
    usedMethods.add(titleize(BillDeliveryMethod.Paper))
  }

  // Order the methods consistently: Printing, Paper, Email, Text.
  const orderedMethods = [PRINTING_LABEL, ...DELIVERY_METHOD_ORDER.map(method => titleize(method))].filter(label =>
    usedMethods.has(label)
  )
  const deliveryMethodLabel = orderedMethods?.length
    ? ` via ${orderedMethods.join(', ')}`
    : wasManuallyMarked
    ? ' (manually marked)'
    : ''

  return (
    <CardBox mb={3}>
      <Box
        sx={{
          p: 3,
          borderBottom: '1px solid #E4E7EC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5}>
          <Typography variant="h6" fontWeight={400} color="gray900" fontSize="18px">
            Delivery Details
          </Typography>
          {isDelivered && deliveredDate && (
            <Chip
              icon={<CheckCircle />}
              label={`Delivered to client${deliveryMethodLabel} · ${format(deliveredDate, 'MMM dd, yyyy')}`}
              size="small"
              color="success"
              variant="outlined"
            />
          )}
        </Stack>
        {canMarkDelivered && !isDelivered && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowMarkDeliveredConfirmation(true)}
            disabled={markingDelivered}
            endIcon={markingDelivered && <CircularProgress size={16} color="inherit" />}
          >
            Mark as Delivered
          </Button>
        )}
      </Box>

      {deliverableParties.length > 0 ? (
        <>
          {deliverableParties.map((party, index) => {
            const methods = DELIVERY_METHOD_ORDER.filter(method => party.billDeliveryMethods?.includes(method))

            return (
              <Box key={index} px={3.5} py={2} borderBottom="1px solid #E4E7EC">
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1.5}>
                  <Typography color="secondary.light" fontWeight={500}>
                    {getClientName(party)}
                  </Typography>
                  {party.caseRole && (
                    <Chip label={enumToLabel(party.caseRole)} size="small" variant="outlined" color="secondary" />
                  )}
                </Stack>

                <Stack gap={1.25}>
                  {methods.map(method => {
                    const { label, icon, target } = getMethodConfig(method, party)
                    const isActiveMethod = usedMethods.has(label)
                    return (
                      <Stack key={method} direction="row" alignItems="flex-start" gap={1.25}>
                        {icon}
                        <Box>
                          <Stack direction="row" alignItems="center" gap={1}>
                            <Typography fontWeight={500} fontSize={13} color="gray900">
                              {label}
                            </Typography>
                            {isActiveMethod && (
                              <Chip
                                icon={<CheckCircle />}
                                label="Delivered"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                              />
                            )}
                          </Stack>
                          <Typography
                            variant="subtitle1"
                            color={target ? 'secondary.light' : 'text.disabled'}
                            sx={{ wordBreak: 'break-word' }}
                          >
                            {target || NOT_SET}
                          </Typography>
                        </Box>
                      </Stack>
                    )
                  })}
                </Stack>
              </Box>
            )
          })}

          {ccEmailBillsTo && (
            <Box px={3.5} py={2}>
              <Typography fontWeight={500} fontSize={13} color="gray900">
                CC Email Bills To
              </Typography>
              <Typography variant="subtitle1" color="secondary.light" sx={{ wordBreak: 'break-word' }}>
                {ccEmailBillsTo}
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <Box px={3.5} py={2}>
          <Typography variant="subtitle1" color="secondary.light">
            No delivery method set for this matter.
          </Typography>
        </Box>
      )}

      {showMarkDeliveredConfirmation && (
        <ConfirmationModal
          isOpen={showMarkDeliveredConfirmation}
          title="Mark Bill as Delivered"
          message={
            `Confirm that bill #${billId} has been delivered to the client.\n\n` +
            'This marks the bill as sent to the client and records the sent date. This cannot be undone.'
          }
          confirmText="Mark as Delivered"
          cancelText="Cancel"
          variant="danger"
          isLoading={markingDelivered}
          onConfirm={() => billId != null && markBillDelivered({ variables: { billId: Number(billId) } })}
          onCancel={() => setShowMarkDeliveredConfirmation(false)}
        />
      )}
    </CardBox>
  )
}

export default BillDeliveryDetailsCard
